/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-11-05 17:07:10
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import { removeUnexpectedCharsInTerms, checkHasSpecialCharacters } from 'src/helpers';

/**
 * Build synonyms before saving
 *
 * @param  {array} synonyms
 *                 [
 *                   {
 *                     searchTerm: <string>,
 *                     biDirection: <boolean>,
 *                     synonyms: <string>,
 *                     allowSuggestion: <boolean>
 *                   }
 *                 ]
 *
 * @return {array}  save synonyms
 */
export const buildSynonyms = synonyms => {
  // Remove special characters
  const saveSynonyms = _.map(synonyms, synonym => {
    return {
      ...synonym,
      searchTerm: synonym.searchTerm,
      biDirection: synonym.biDirection === '<=>' ? true : false,
      synonyms: buildSynonymsArray(synonym.synonyms),
      allowSuggestion: synonym.allowSuggestion
    };
  });

  return saveSynonyms;
};

/**
 * Build synonyms array in a synonym
 *
 * @param  {string} synonyms string
 * @return {array}           list of synonyms
 */
export const buildSynonymsArray = synonyms => {
  if (_.isEmpty(synonyms)) {
    return [];
  }
  let saveSynonyms = _.isArray(synonyms) ? synonyms : synonyms.split(',');

  // Remove empty item
  saveSynonyms = _.filter(saveSynonyms, text => !_.isEmpty(text.trim()));

  return saveSynonyms;
};

/**
 * Check the synonyms list is empty
 *
 * @param  {array} synonyms
 *                 [
 *                   {
 *                     searchTerm: <string>,
 *                     biDirection: <boolean>,
 *                     synonyms: <string>,
 *                     allowSuggestion: <boolean>
 *                   }
 *                 ]
 *
 * @return {boolean}  the list is empty or not
 */
export const isEmptySynonyms = synonyms => {
  const synonym = _.find(synonyms, item => {
    let { searchTerm, synonyms } = item;
    searchTerm = removeUnexpectedCharsInTerms(searchTerm);
    synonyms = buildSynonymsArray(synonyms);

    return !_.isEmpty(searchTerm) && !_.isEmpty(synonyms);
  });

  return synonym ? false : true;
};

/**
 * Check validate synonyms
 *
 * @param  {array} synonyms
 *                 [
 *                   {
 *                     searchTerm: <string>,
 *                     biDirection: <boolean>,
 *                     synonyms: <string>,
 *                     allowSuggestion: <boolean>
 *                   }
 *                 ]
 *
 */
export const checkValidateSynonyms = synonyms => {
  for (let synonym of synonyms) {
    // Search term
    if (/[>,=]/.test(synonym.searchTerm)) {
      return 'AdminHTML.Synonym.Validate.invalid_SearchTerm_SpecialCharacter01';
    }

    let checkSpecialCharactersValue = checkHasSpecialCharacters(synonym.searchTerm);
    if (
      (_.isEmpty(synonym.searchTerm.trim()) || checkSpecialCharactersValue) &&
      !_.isEmpty(synonym.id)
    ) {
      return `AdminHTML.Synonym.Validate.invalid_SearchTerm_SpecialCharacter02`;
    }

    if (synonym.searchTerm.length > 128) {
      return 'AdminHTML.Synonym.Validate.invalid_SearchTerm_Length';
    }
    // Synonyms
    const synonyms = buildSynonymsArray(synonym.synonyms);
    if (_.isEmpty(synonyms) && !_.isEmpty(synonym.id)) {
      return `AdminHTML.Synonym.Validate.invalid_Synonyms_SpecialCharacter02`;
    }

    for (let item of synonyms) {
      checkSpecialCharactersValue = checkHasSpecialCharacters(item);
      if (checkSpecialCharactersValue) {
        return `AdminHTML.Synonym.Validate.invalid_Synonyms_SpecialCharacter02`;
      }
      if (/[>=]/.test(item)) {
        return 'AdminHTML.Synonym.Validate.invalid_Synonyms_SpecialCharacter01';
      }
      if (item.length > 128) {
        return 'AdminHTML.Synonym.Validate.invalid_Synonyms_Length';
      }
    }
  }
  return '';
};
